import React, { FormEvent, useState } from 'react';
import './ChatSendMessage.scss';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { DossierMember } from '../../models/DossierMember';
import { NotificationHelper } from '../../helpers/NotificationHelper';

interface ChatSendMessageProps {
  sendingMessage?: boolean;
  notifyNowMembers?: DossierMember[];
  className?: string;
  onSend: (message: string, options?: { notifyNow?: boolean }) => Promise<void>;
}

export function ChatSendMessage({ sendingMessage, notifyNowMembers, className, onSend }: ChatSendMessageProps): JSX.Element {
  const [message, setMessage] = useState<string>('');
  const [notifyNow, setNotifyNow] = useState<boolean>(false);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const sanitizedValue = message.trim(); // Other sanitizing is handled by React
    if (sanitizedValue.length !== 0) {
      onSend(sanitizedValue, { notifyNow })
        .then(() => {
          setMessage('');
          if (notifyNow) {
            NotificationHelper.toastSuccess('Mail verstuurd', { silent: true });
          }
          setNotifyNow(false);
        })
        .catch(() => console.warn('Not clearing chat since message was not send'));
    } else {
      setMessage('');
    }
  };

  const renderTooltipContent = () => {
    const members = [...(notifyNowMembers || [])];
    const last = members.pop();
    if (last && members.length === 0) {
      return <>
        Zodra je dit bericht verstuurt, wordt er een mail gestuurd naar <strong>{last.user.name}</strong> dat er een nieuw bericht klaarstaat.
      </>;
    } else if (!last) {
      return <>
        Zodra je dit bericht verstuurt, wordt er een mail gestuurd dat er een nieuw bericht klaarstaat.
      </>;
    }
    return <>
      Zodra je dit bericht verstuurt, wordt er een mail gestuurd naar {members.map((member, index) => <span key={member.memberId}><strong>{member.user.name}</strong>{index === members.length - 1 ? '' : ', '}</span>)} en <strong>{last.user.name}</strong> dat er een nieuw bericht voor hen klaarstaat.
    </>;
  };

  const fullClassName = `chat-send-message ${className || ''}`;
  return (
    <Form className={fullClassName} onSubmit={handleSubmit}>
      <div className="chat-send-message__row">
        <Form.Group className="chat-send-message__textbox" controlId="sendMessage">
          <Form.Control
            className="chat-send-message__textbox-input"
            autoFocus={true}
            as="textarea"
            rows={2}
            value={message}
            onChange={event => setMessage(event.target.value)}
            placeholder="Schrijf een bericht..."
            disabled={sendingMessage}
          />
        </Form.Group>
        <button title="Send chat message" className="chat-send-message__send" type="submit" disabled={sendingMessage}>
          {
            sendingMessage ?
              <Spinner animation="border" variant="light" /> : <></>
          }
        </button>
      </div>
      {
        !!notifyNowMembers?.length &&
        <OverlayTrigger placement="top" overlay={
          <Tooltip id="tooltip-notify-now">
            { renderTooltipContent() }
          </Tooltip>
        }>
          <Form.Group controlId="notify-now" className="chat-send-message__notify-now-container">
            <Form.Check type="checkbox" label="Verstuur een mail nadat je bericht is verstuurd" checked={notifyNow} onChange={event => setNotifyNow(event.currentTarget.checked)} />
          </Form.Group>
        </OverlayTrigger>
      }
    </Form>
  );
}
